import { supabase } from "@/lib/supabase";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { Recipe } from "@/features/Recipes-renamed/types";
import { mapDatabaseRecipeToRecipe, type RecipesRowWithRelations } from "@/lib/mapDatabaseRecipeToRecipe";
import { useAuthContext } from "@/features/authentication/useAuthContext";
import type { RecipeTypeRow } from "./useGetRecipeTypes";

export interface TypeShelfSample {
    recipeType: RecipeTypeRow;
    recipe: Recipe;
    totalCount: number;
}

function shuffle<T>(items: T[]): T[] {
    const arr = items.slice();
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

export function useTypeShelfSamples(count: number): UseQueryResult<TypeShelfSample[]> {
    const { user } = useAuthContext();
    return useQuery({
        enabled: !!user,
        queryKey: ["typeShelfSamples", count],
        staleTime: 0,
        gcTime: 0,
        queryFn: async () => {
            const { data, error } = await supabase
                .from("recipes")
                .select(
                    `
                        *,
                        recipe_types (*),
                        recipe_categories (*),
                        ingredient_groups (*, ingredients (*))
                    `
                )
                .eq("user_id", user!.id);

            if (error || !data) {
                console.error("Error fetching type shelf samples:", error);
                throw error;
            }

            const recipes = data.map((row) =>
                mapDatabaseRecipeToRecipe(row as unknown as RecipesRowWithRelations)
            );

            const buckets = new Map<number, { recipeType: RecipeTypeRow; recipes: Recipe[] }>();
            for (const recipe of recipes) {
                if (!recipe.recipeType) continue;
                const existing = buckets.get(recipe.recipeType.id);
                if (existing) {
                    existing.recipes.push(recipe);
                } else {
                    buckets.set(recipe.recipeType.id, {
                        recipeType: recipe.recipeType,
                        recipes: [recipe]
                    });
                }
            }

            const candidates = shuffle(
                Array.from(buckets.values()).filter((b) => b.recipes.length > 0)
            );

            const used = new Set<number>();
            const samples: TypeShelfSample[] = [];
            for (const { recipeType, recipes: bucketRecipes } of candidates) {
                if (samples.length >= count) break;
                const pick = shuffle(bucketRecipes).find((r) => !used.has(r.id));
                if (!pick) continue;
                used.add(pick.id);
                samples.push({ recipeType, recipe: pick, totalCount: bucketRecipes.length });
            }

            return samples;
        }
    });
}
